import { request, Group, Player, Coach } from "./client";
import { registerMessages } from "./errors";

registerMessages({
  "failed to list training sessions": "Не удалось загрузить тренировки",
  "failed to create training session": "Не удалось создать тренировку",
  "failed to update training session": "Не удалось сохранить тренировку",
  "failed to delete training session": "Не удалось удалить тренировку",
  "failed to bulk update training time": "Не удалось поменять время следующих тренировок",
  "invalid coach_id": "Некорректный тренер",
  "invalid start_date": "Некорректная дата начала периода",
  "invalid end_date": "Некорректная дата конца периода",
  "failed to get attendance": "Не удалось загрузить посещаемость",
  "session_ids query parameter required": "Не указаны тренировки",
  "failed to batch upsert attendance": "Не удалось сохранить посещаемость",
  "failed to update attendance points": "Не удалось сохранить баллы",
  "failed to recalculate points": "Не удалось пересчитать итоговые баллы детей",
  "failed to list schedules": "Не удалось загрузить недельное расписание",
  "failed to create schedule": "Не удалось сохранить недельное расписание",
  "failed to update schedule": "Не удалось сохранить недельное расписание",
  "failed to list scheduled trainings": "Не удалось загрузить запланированные тренировки",
  "failed to delete scheduled training": "Не удалось удалить запланированную тренировку",
  "failed to convert scheduled training": "Не удалось сделать тренировку из запланированной",
  "failed to list places": "Не удалось загрузить места",
});

export type TrainingType = "training" | "game";

export interface Training {
  id: string;
  date: string;
  title: string;
  group_id: string | null;
  type: string;
  coach_id: number | null;
  place_id?: string | null;
  start_time?: string | null;
  end_time?: string | null;
  place_name?: string | null;
  place_address?: string | null;
  coach_name?: string | null;
  group_name?: string | null;
  created_at: string;
}

// PUT /api/trainings/{id} overwrites every column, so all of them are sent.
export interface TrainingFields {
  date: string;
  title: string;
  type: TrainingType | string;
  coach_id: number | null;
  place_id: string | null;
  start_time: string | null;
  end_time: string | null;
}

export interface Place {
  id: string;
  name: string;
  address?: string | null;
  notes?: string | null;
  created_at: string;
}

export interface Attendance {
  id: string;
  player_id: string;
  session_id: string;
  attended: boolean;
  points: number;
  created_at: string;
}

export interface AttendanceWrite {
  player_id: string;
  session_id: string;
  attended: boolean;
  points: number;
}

// Weekdays are ISO numbers, 1 = Monday … 7 = Sunday, as the app stores them.
export interface TrainingSchedule {
  id: string;
  group_id: string;
  title: string;
  weekdays: number[];
  start_time: string;
  end_time: string;
  created_at: string;
  updated_at: string;
}

export interface ScheduleFields {
  group_id: string;
  title: string;
  weekdays: number[];
  start_time: string;
  end_time: string;
}

export interface ScheduledTraining {
  id: string;
  group_id: string;
  date: string;
  title: string;
  start_time: string;
  end_time: string;
  schedule_id: string;
  created_at: string;
}

export interface GenerateTrainings {
  group_id: string;
  title: string;
  weekdays: number[];
  start_date: string;
  end_date: string;
  type: "training";
  start_time: string;
  end_time: string;
  coach_id: number | null;
}

export interface BulkTimeChange {
  group_id: string;
  weekday: number;
  after_date: string;
  start_time?: string;
  end_time?: string;
}

const id = (value: string) => encodeURIComponent(value);

export const trainingsApi = {
  listGroups: () => request<Group[] | null>("/api/groups/"),
  listPlaces: () => request<Place[] | null>("/api/places/"),
  listCoaches: () => request<Coach[] | null>("/api/coaches/"),
  // Inactive players come too: their old attendance still has to show.
  listGroupPlayers: (groupID: string) =>
    request<Player[] | null>(`/api/players/?${new URLSearchParams({ group_id: groupID })}`),

  listTrainings: (groupID: string, start: string, end: string) =>
    request<Training[] | null>(`/api/trainings/?${new URLSearchParams({ group_id: groupID, start, end })}`),

  // The server pushes a notification to the group's children.
  createTraining: (groupID: string, fields: TrainingFields) =>
    request<Training>("/api/trainings/", {
      method: "POST",
      body: JSON.stringify({ ...fields, group_id: groupID }),
    }),

  updateTraining: (trainingID: string, fields: TrainingFields) =>
    request<{ ok: boolean }>(`/api/trainings/${id(trainingID)}`, { method: "PUT", body: JSON.stringify(fields) }),

  // Only trainings (not games) of the group on that ISO weekday, from after_date on.
  // A time left out stays as it is.
  changeTimeOnward: (change: BulkTimeChange) =>
    request<{ ok: boolean }>("/api/trainings/bulk-update-time", { method: "PUT", body: JSON.stringify(change) }),

  // Attendance and points cascade with the training; the children get a push.
  deleteTraining: (trainingID: string) => request<void>(`/api/trainings/${id(trainingID)}`, { method: "DELETE" }),

  // The server ignores ?group_id here, so the caller filters.
  listSchedules: () => request<TrainingSchedule[] | null>("/api/schedules/"),

  createSchedule: (fields: ScheduleFields) =>
    request<TrainingSchedule>("/api/schedules/", { method: "POST", body: JSON.stringify(fields) }),

  updateSchedule: (scheduleID: string, fields: ScheduleFields) =>
    request<TrainingSchedule>(`/api/schedules/${id(scheduleID)}`, { method: "PUT", body: JSON.stringify(fields) }),

  // Creates real trainings for every matching day; it does not skip days that
  // already have one.
  generateTrainings: (input: GenerateTrainings) =>
    request<Training[] | null>("/api/trainings/generate", { method: "POST", body: JSON.stringify(input) }),

  // The server ignores ?group_id here too.
  listScheduledTrainings: () => request<ScheduledTraining[] | null>("/api/scheduled-trainings/"),

  convertScheduledTraining: (scheduledID: string) =>
    request<Training>(`/api/scheduled-trainings/${id(scheduledID)}/convert`, { method: "POST" }),

  deleteScheduledTraining: (scheduledID: string) =>
    request<void>(`/api/scheduled-trainings/${id(scheduledID)}`, { method: "DELETE" }),

  listAttendance: (trainingID: string) =>
    request<Attendance[] | null>(`/api/trainings/${id(trainingID)}/attendance`),

  // Upsert by (player, training): attended and points are written as sent,
  // rows of players left out stay untouched. Totals are not recalculated.
  saveAttendance: (records: AttendanceWrite[]) =>
    request<{ ok: boolean }>("/api/attendance/batch", { method: "POST", body: JSON.stringify(records) }),

  // The server also sets attended = points > 0 and recalculates totals, so
  // this is only safe for a present child getting points above zero.
  setPoints: (attendanceID: string, points: number) =>
    request<{ ok: boolean }>(`/api/attendance/${id(attendanceID)}/points`, {
      method: "PUT",
      body: JSON.stringify({ points }),
    }),

  recalculatePoints: () => request<{ ok: boolean }>("/api/players/recalculate-points", { method: "POST" }),
};

// Dates travel as "YYYY-MM-DD" strings and are handled as strings: a Date
// built from one is UTC midnight and shows the previous day west of Greenwich.

const pad = (n: number) => String(n).padStart(2, "0");

export const dayString = (year: number, month: number, day: number) => `${year}-${pad(month)}-${pad(day)}`;

export function parseDay(value: string): { year: number; month: number; day: number } {
  const [year, month, day] = value.slice(0, 10).split("-").map(Number);
  return { year, month, day };
}

export function today(): string {
  const now = new Date();
  return dayString(now.getFullYear(), now.getMonth() + 1, now.getDate());
}

export function daysInMonth(year: number, month: number): number {
  if (month === 2) return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0 ? 29 : 28;
  return [4, 6, 9, 11].includes(month) ? 30 : 31;
}

// ISO weekday (1 = Monday … 7 = Sunday) by Sakamoto's method, without a Date.
export function isoWeekday(value: string): number {
  const { year, month, day } = parseDay(value);
  const offsets = [0, 3, 2, 5, 0, 3, 5, 1, 4, 6, 2, 4];
  const y = month < 3 ? year - 1 : year;
  const sundayBased = (y + Math.floor(y / 4) - Math.floor(y / 100) + Math.floor(y / 400) + offsets[month - 1] + day) % 7;
  return sundayBased === 0 ? 7 : sundayBased;
}

export function nextDay(value: string): string {
  const { year, month, day } = parseDay(value);
  if (day < daysInMonth(year, month)) return dayString(year, month, day + 1);
  return month === 12 ? dayString(year + 1, 1, 1) : dayString(year, month + 1, 1);
}

export function formatDay(value: string): string {
  const { year, month, day } = parseDay(value);
  return `${pad(day)}.${pad(month)}.${year}`;
}

export const WEEKDAYS = [
  { iso: 1, short: "Пн", name: "понедельник", accusative: "понедельник" },
  { iso: 2, short: "Вт", name: "вторник", accusative: "вторник" },
  { iso: 3, short: "Ср", name: "среда", accusative: "среду" },
  { iso: 4, short: "Чт", name: "четверг", accusative: "четверг" },
  { iso: 5, short: "Пт", name: "пятница", accusative: "пятницу" },
  { iso: 6, short: "Сб", name: "суббота", accusative: "субботу" },
  { iso: 7, short: "Вс", name: "воскресенье", accusative: "воскресенье" },
];

export const weekdayOf = (value: string) => WEEKDAYS[isoWeekday(value) - 1];

export function formatTimeRange(start?: string | null, end?: string | null): string {
  if (start && end) return `${start}–${end}`;
  return start || (end ? `до ${end}` : "");
}
